/**
 * Holds the app behind a birthday, once a day.
 *
 * Sits outside the daily check-in rather than inside it: a birthday is not a
 * mood question, and stacking the two would mean three screens before the
 * dashboard. The birthday plays first and the check-in follows underneath it.
 *
 * Fails open on everything. A bad date, a failed request, an error inside the
 * moment — all of them land on "no occasion today", because nothing about a
 * birthday is worth keeping somebody out of their work.
 */
import { ReactNode, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import apiClient from '../../services/api/config';
import { useAuth } from '../../contexts/AuthContext';
import BirthdayMoment from './BirthdayMoment';
import { Occasion, localDateISO, readOccasionMark, writeOccasionMark } from './occasion.logic';

// TEST MODE — these accounts always see a birthday, with made-up people, on
// every full page load. Empty in normal operation.
const FORCE_OCCASION_EMAILS: string[] = [];
let forcedOccasionShownThisLoad = false;

const SAMPLE: Occasion[] = [
  { kind: 'self', name: 'you', relationship: null, turning: 34 },
];

export default function OccasionGate({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id;
  const today = localDateISO();

  const [seen, setSeen] = useState(() => readOccasionMark(userId, today));
  useEffect(() => { setSeen(readOccasionMark(userId, today)); }, [userId, today]);

  const forced =
    !!user &&
    !forcedOccasionShownThisLoad &&
    FORCE_OCCASION_EMAILS.includes((user.email || '').toLowerCase());

  const blocked = !user || user.role === 'super-super-admin' || !!user.mustChangePassword || seen;

  const query = useQuery({
    queryKey: ['occasions', userId, today],
    queryFn: async () => {
      const r = await apiClient.get('/onboarding/occasions', { params: { date: today } });
      return (r.data?.data?.occasions ?? []) as Occasion[];
    },
    enabled: isAuthenticated && !!user && (!blocked || forced),
    staleTime: Infinity,
    retry: 1,
  });

  const occasions = forced ? SAMPLE : (query.data ?? []);
  const show = (forced || !blocked) && query.isFetched && occasions.length > 0;

  const done = () => {
    if (forced) forcedOccasionShownThisLoad = true;
    else writeOccasionMark(userId, today);
    setSeen(true);
  };

  // Nothing is held while the lookup is in flight: a birthday is worth a
  // moment, never worth a blank screen.
  if (!show) return <>{children}</>;

  return (
    <ErrorBoundary fallbackRender={() => <>{children}</>} onError={done}>
      <BirthdayMoment
        occasions={occasions}
        firstName={user?.firstName || ''}
        userId={userId}
        onDone={done}
      />
    </ErrorBoundary>
  );
}
